import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { PlanRiderDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import {
  DRAWER_ON_BENEFIT_CARD,
  DRAWER_ON_MY_BENEFITS,
  DRAWER_ON_BENEFIT_MOBILE_CARD,
  DRAWER_ON_REVIEW,
  getRiderCoverage,
} from './utils';
import { RiderLinesContainerStyled } from './RiderBenefitsStyled';
import { RiderBenefitItem } from '.';
import { DrawerDialog } from '..';
import { DrawerWrapperStyled } from 'enrollment/features/Plans/PlanDetailsDrawer/PlanDetailsDrawerStyled';
import { RiderCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';

export interface RiderBenefitDrawerProps {
  riders: PlanRiderDto[];
  riderCoverages?: RiderCoverageDto[];
  orgPlanYearId: number;
  drawerOrigin: number;
  electedRiders?: number[];
  setElectedRiders?: Dispatch<SetStateAction<number[]>>;
  costMap?: Map<number, number>;
  setCostMap?: Dispatch<SetStateAction<Map<number, number>>>;
}

export const RiderBenefitDrawer = ({
  riders,
  riderCoverages,
  orgPlanYearId,
  drawerOrigin,
  electedRiders,
  setElectedRiders,
  costMap,
  setCostMap,
}: RiderBenefitDrawerProps) => {
  const { t } = useTranslation('common');
  let mobileBenefitsDrawer = false;
  const [pendingRiders, setPendingRiders] = useState(electedRiders || []);
  const [pendingCosts, setPendingCost] = useState(
    costMap || new Map<number, number>()
  );
  const [resetPendingRiders, setResetPendingRiders] = useState(false);
  if (drawerOrigin === DRAWER_ON_BENEFIT_MOBILE_CARD) {
    mobileBenefitsDrawer = true;
  }

  //used for making rider elections inside of the drawer. Needed so that cancel and save buttons work correctly
  // currently only used in mobile
  const customMobileSaveAction = () => {
    if (
      setElectedRiders &&
      mobileBenefitsDrawer &&
      riderCoverages &&
      costMap &&
      setCostMap &&
      pendingRiders
    ) {
      setElectedRiders(pendingRiders);
      setCostMap(pendingCosts);
    }
  };

  const customMobileCloseAction = () => {
    setResetPendingRiders(true);
  };

  useEffect(() => {
    if (electedRiders) {
      setPendingRiders(electedRiders);
    }
    if (costMap) {
      setPendingCost(costMap);
    }
  }, [electedRiders, costMap, setPendingRiders, setPendingCost]);

  const chooseLabel = (electedRiders: number[] | undefined) => {
    switch (drawerOrigin) {
      case DRAWER_ON_BENEFIT_CARD:
        return t('enrollment.riderCovCardLink', { value: riders.length });
      case DRAWER_ON_BENEFIT_MOBILE_CARD:
        return t('enrollment.riderCovMobileCardLink', {
          value:
            electedRiders && electedRiders.length > 0
              ? electedRiders.length
              : '',
        });
      case DRAWER_ON_MY_BENEFITS:
      case DRAWER_ON_REVIEW:
        return t('enrollment.riderMyBenefitsLink', { value: riders.length });
      default:
        return t('enrollment.riderReviewLink');
    }
  };

  if (riders.length < 1) {
    return null;
  }

  return (
    <DrawerWrapperStyled data-testid="rider-drawer-container">
      <DrawerDialog
        id={`rider-drawer-${riders[0].rider_benefit_id}-${riders[0].org_benefit_id}`}
        triggerLabel={chooseLabel(electedRiders)}
        dialogLarge
        dialogTriggerClass="btn-link"
        dialogTriggerSecondary
        dialogHeading={
          mobileBenefitsDrawer
            ? t('enrollment.mobileDrawerRiderHeader')
            : t('enrollment.drawerRiderHeader')
        }
        {...(!mobileBenefitsDrawer
          ? { hideFooter: true }
          : {
              okButtonLabel: t('save'),
              closeButtonLabel: t('cancel'),
              customSaveAction: customMobileSaveAction,
              customCloseAction: customMobileCloseAction,
            })}
      >
        <>
          {!mobileBenefitsDrawer && (
            <p>{t('enrollment.riderDrawerHeader', { value: riders.length })}</p>
          )}
          <RiderLinesContainerStyled>
            {riders.map((rider, index) => {
              return (
                <RiderBenefitItem
                  riderInfo={rider}
                  riderCoverage={getRiderCoverage(rider, riderCoverages)}
                  orgPlanYearId={orgPlanYearId}
                  displayCheckbox={mobileBenefitsDrawer}
                  electedRiders={pendingRiders}
                  setElectedRiders={setPendingRiders}
                  setCostMap={setPendingCost}
                  costMap={pendingCosts}
                  key={index}
                  resetElectedRiders={resetPendingRiders}
                  setResetElectedRiders={setResetPendingRiders}
                  ridersForResetting={electedRiders}
                />
              );
            })}
          </RiderLinesContainerStyled>
        </>
      </DrawerDialog>
    </DrawerWrapperStyled>
  );
};
